import { createTheme } from '@mui/material/styles';

const palette = {
  primary: {
    main: '#1B7ADA',      // Primary blue
    light: '#8ACAFF',
    dark: '#1D4C7B',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#1AC447',      // Secondary green
    light: '#9CFD9A',
    dark: '#217739',
    contrastText: '#ffffff',
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  grey: {
    50:  '#F7F7F7',
    100: '#DCDCDD',
    200: '#C5C3C6',
    300: '#767679',
    400: '#27292B',
  },
  background: {
    default: '#ffffff',
    paper: '#f5f5f5',
  },
  text: {
    primary: '#767679',
    secondary: '#424242',
    disabled: '#F7F7F7',
    hover: '#27292B',
  },
};

const theme = createTheme({
  palette,
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    // Typography scale with balanced sizes
    h1: {
      fontSize: '3rem',        // 48px
      fontWeight: 700,
      lineHeight: 1.2,
      '@media (max-width:600px)': {
        fontSize: '2.5rem',    // 40px for mobile
      },
    },
    h2: {
      fontSize: '2.5rem',      // 40px
      fontWeight: 600,
      lineHeight: 1.25,
      '@media (max-width:600px)': {
        fontSize: '2rem',      // 32px for mobile
      },
    },
    h3: {
      fontSize: '2rem',        // 32px
      fontWeight: 700,
      lineHeight: 1.3,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',   // 28px for mobile
      },
    },
    h4: {
      fontSize: '1.5rem',      // 24px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',     // 20px
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.125rem',    // 18px
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1.125rem',    // 18px
      lineHeight: 1.5,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '1rem',        // 16px
      lineHeight: 1.57,
      fontWeight: 500,
    },
    body1: {
      fontSize: '1.25rem',     // 20px
      lineHeight: 1.57,
    },
    body2: {
      fontSize: '1rem',        // 16px
      lineHeight: 1.6,
    },
    button: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',     // 12px
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',     // 12px
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '16px',
        },
        body: {
          lineHeight: 1.6,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '& a': {
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
        paragraph: {
          marginBottom: '1rem',
        },
        gutterBottom: {
          marginBottom: '1rem',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '24px',
          paddingRight: '24px',
          [breakpoints => breakpoints.up('sm')]: {
            paddingLeft: '32px',
            paddingRight: '32px',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          padding: '10px 16px',
        },
        filled: {
          '& .MuiAlertTitle-root': {
            fontWeight: 600,
            marginBottom: '4px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.chat-message': {
            borderRadius: '16px',
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '&.chat-input': {
            backgroundColor: 'white',
          }
        }
      }
    },
    // Consistent styling for Accordion
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0 16px',
          '&.Mui-expanded': {
            minHeight: '48px',
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '8px 16px 16px',
        },
      },
    },
  },
});

export default theme;